import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { SectionCard, EmptyState, StatusBadge } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

const PURPOSE_LABEL = { geburtstag: "Geburtstag", zufriedenheit: "Zufriedenheit", werbung: "Werbung" };
const MSG_STATUS = {
  geplant: { label: "Geplant", cls: "bg-muted text-muted-foreground" },
  gesendet: { label: "Gesendet", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300" },
  zugestellt: { label: "Zugestellt", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  gelesen: { label: "Gelesen", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  beantwortet: { label: "Beantwortet", cls: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300" },
  fehlgeschlagen: { label: "Fehlgeschlagen", cls: "bg-red-500/10 text-red-700 dark:text-red-300" },
  uebersprungen: { label: "Übersprungen", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
};
const FB_STATUS = {
  offen: { label: "Offen", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  in_bearbeitung: { label: "In Bearbeitung", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300" },
  abgeschlossen: { label: "Abgeschlossen", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
};

function useCustomers() {
  return useQuery({ queryKey: ["customers-min-wa"], queryFn: async () => (await api.get("/customers?limit=200")).data });
}

/* ----------------------------- Einwilligungen ----------------------------- */
export function CommConsents() {
  const qc = useQueryClient();
  const { data: cust } = useCustomers();
  const [cid, setCid] = useState("");
  const { data } = useQuery({
    queryKey: ["wa-consents", cid], enabled: !!cid,
    queryFn: async () => (await api.get(`/wa/consents?customer_id=${cid}`)).data,
  });
  const purposes = ["geburtstag", "zufriedenheit", "werbung"];
  const byPurpose = Object.fromEntries((data?.items || []).map((c) => [c.purpose, c]));

  const setConsent = async (purpose, status) => {
    try { await api.post("/wa/consents", { customer_id: cid, purpose, status, source: "manuell" }); toast.success("Einwilligung aktualisiert"); qc.invalidateQueries({ queryKey: ["wa-consents", cid] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const importAll = async () => {
    try { const r = await api.post("/wa/consents/import"); toast.success(`${r.data.created} Einwilligungen importiert`); qc.invalidateQueries({ queryKey: ["wa-consents"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SectionCard title="Einwilligungen & Datenschutz" action={<Button size="sm" variant="outline" onClick={importAll} data-testid="wa-consent-import">Bestehende Kennzeichen importieren</Button>}>
      <p className="mb-3 text-xs text-muted-foreground">Einwilligung je Zweck getrennt (DSGVO / § 7 UWG). Ein Widerruf verhindert weitere Nachrichten dieses Zwecks.</p>
      <div className="mb-4 max-w-sm space-y-1">
        <Label className="text-xs">Kunde</Label>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="wa-consent-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
          <SelectContent>{(cust?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {cid && (
        <div className="space-y-2">
          {purposes.map((p) => {
            const c = byPurpose[p];
            const granted = c?.status === "granted";
            return (
              <div key={p} className="flex items-center justify-between rounded-md border p-3" data-testid={`wa-consent-${p}`}>
                <div>
                  <div className="text-sm font-medium">{PURPOSE_LABEL[p]}</div>
                  <div className="text-xs text-muted-foreground">
                    {c ? `${granted ? "Erteilt" : "Widerrufen"} · Quelle: ${c.source}` : "Keine Einwilligung"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={granted ? "secondary" : "default"} className={!granted ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""} onClick={() => setConsent(p, "granted")}>Erteilen</Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setConsent(p, "revoked")}>Widerrufen</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ----------------------------- Nachrichten & Historie ----------------------------- */
export function CommMessages({ templates, status }) {
  const { data: cust } = useCustomers();
  const [cid, setCid] = useState("");
  const { data, refetch } = useQuery({ queryKey: ["wa-messages"], queryFn: async () => (await api.get("/wa/messages")).data });
  const approvedSurvey = (templates || []).filter((t) => t.status === "genehmigt" && t.purpose === "zufriedenheit");

  const sendSurvey = async () => {
    if (!cid) return toast.error("Bitte Kunde wählen");
    try { await api.post("/wa/send/survey", { customer_id: cid }); toast.success("Umfrage gesendet"); refetch(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Manuelle Zufriedenheitsabfrage senden">
        {status !== "connected" && <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">WhatsApp nicht verbunden – Versand ist deaktiviert.</p>}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1"><Label className="text-xs">Kunde</Label>
            <Select value={cid} onValueChange={setCid}>
              <SelectTrigger data-testid="wa-send-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>{(cust?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button onClick={sendSurvey} disabled={status !== "connected" || approvedSurvey.length === 0} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-send-survey">Umfrage senden</Button>
        </div>
        {approvedSurvey.length === 0 && <p className="mt-2 text-xs text-muted-foreground">Es ist keine genehmigte Umfrage-Vorlage vorhanden.</p>}
      </SectionCard>

      <SectionCard title="Versandhistorie">
        {(data?.items || []).length === 0 ? <EmptyState title="Keine Nachrichten" /> : (
          <div className="space-y-2">
            {data.items.map((m) => (
              <div key={m.id} className="flex flex-col gap-1 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`wa-msg-${m.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{m.customer_name || m.to || "—"}</span>
                    <Badge variant="outline" className="text-[10px]">{m.automation || m.direction}</Badge>
                    {m.template && <span className="text-xs text-muted-foreground">{m.template}</span>}
                  </div>
                  {m.error && <p className="text-xs text-destructive">{m.error}</p>}
                  <p className="text-[11px] text-muted-foreground">{fmtDateTime(m.created_at)}</p>
                </div>
                <StatusBadge status={m.status} map={MSG_STATUS} />
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ----------------------------- Feedback ----------------------------- */
export function CommFeedback() {
  const qc = useQueryClient();
  const [onlyNeg, setOnlyNeg] = useState(false);
  const [edit, setEdit] = useState(null);
  const { data } = useQuery({ queryKey: ["wa-feedback", onlyNeg], queryFn: async () => (await api.get(`/wa/feedback?negative=${onlyNeg}`)).data });

  const save = async () => {
    try { await api.put(`/wa/feedback/${edit.id}`, { feedback_status: edit.feedback_status, internal_note: edit.internal_note, resolution: edit.resolution }); toast.success("Feedback aktualisiert"); setEdit(null); qc.invalidateQueries({ queryKey: ["wa-feedback"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SectionCard title="Kundenfeedback" action={
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={onlyNeg} onChange={(e) => setOnlyNeg(e.target.checked)} data-testid="wa-feedback-neg-filter" /> Nur negative (1–2)</label>
    }>
      {(data?.items || []).length === 0 ? <EmptyState title="Kein Feedback" /> : (
        <div className="space-y-2">
          {data.items.map((s) => (
            <div key={s.id} className={`rounded-md border p-3 ${s.rating <= 2 && s.rating ? "border-red-500/30 bg-red-500/5" : ""}`} data-testid={`wa-feedback-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{s.customer_name || "Kunde"}</span>
                  {s.rating ? <Badge className={s.rating <= 2 ? "bg-red-500/10 text-red-700 dark:text-red-300" : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}>{s.rating}/5</Badge> : <Badge variant="outline">Keine Bewertung</Badge>}
                  <StatusBadge status={s.feedback_status || "offen"} map={FB_STATUS} />
                  <Badge variant="outline" className="text-[10px]">{s.trigger}</Badge>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEdit({ ...s })}>Bearbeiten</Button>
              </div>
              {s.internal_note && <p className="mt-1 text-xs text-muted-foreground">Notiz: {s.internal_note}</p>}
              {s.task_id && <p className="mt-1 text-[11px] text-muted-foreground">Verknüpfte ERP-Aufgabe erstellt.</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Feedback bearbeiten</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Bearbeitungsstatus</Label>
                <Select value={edit.feedback_status || "offen"} onValueChange={(v) => setEdit({ ...edit, feedback_status: v })}>
                  <SelectTrigger data-testid="wa-feedback-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FB_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Interne Notiz</Label><Textarea rows={2} value={edit.internal_note || ""} onChange={(e) => setEdit({ ...edit, internal_note: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Lösung</Label><Textarea rows={2} value={edit.resolution || ""} onChange={(e) => setEdit({ ...edit, resolution: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Abbrechen</Button>
            <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-feedback-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
